import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  CalendarDays,
  ChevronDown,
  Hash,
  Layers,
  MapPin,
  Package,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import { useLocationName } from "@/api/locations";
import { useDeleteSource, useItemSources, type Source } from "@/api/sources";
import { LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/formatters";

import { SourceForm } from "./SourceForm";

interface SourcesListProps {
  itemId: number;
}

export default function SourcesList({ itemId }: SourcesListProps) {
  const { t } = useTranslation();
  const query = useItemSources(itemId);
  const locationName = useLocationName();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Source | null>(null);

  const grouped = React.useMemo(() => {
    const out: Record<number, Source[]> = {};
    (query.data ?? []).forEach((s) => {
      (out[s.location] = out[s.location] ?? []).push(s);
    });
    return out;
  }, [query.data]);

  if (query.isLoading) {
    return <LoadingState label={t("items.detail.sources.loading")} />;
  }

  const groups = Object.entries(grouped);
  const isEmpty = groups.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <PlusIcon className="mr-1 size-4" />
          {t("sources.newSource")}
        </Button>
      </div>

      {isEmpty ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {t("items.detail.sources.empty")}
        </p>
      ) : (
        groups.map(([locationId, sources]) => (
          <section key={locationId} className="flex flex-col gap-3">
            <h3 className="text-foreground border-b pb-2 text-sm font-semibold">
              {locationName(Number(locationId))}
            </h3>
            <div className="flex flex-col gap-2">
              {sources.map((s) => (
                <SourceCard
                  key={s.id}
                  source={s}
                  onEdit={() => setEditing(s)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <SourceForm
        itemId={itemId}
        source={editing}
        open={formOpen || editing != null}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}

function SourceCard({
  source,
  onEdit,
}: {
  source: Source;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const locationName = useLocationName();

  return (
    <Collapsible className="bg-card overflow-hidden rounded-md border">
      <CollapsibleTrigger
        className={cn(
          "group flex w-full items-center justify-between gap-3 px-4 py-3 text-left",
          "hover:bg-muted/40 transition-colors",
        )}
      >
        <div className="min-w-0">
          <div className="text-foreground flex items-center gap-2 truncate font-medium">
            {source.name}
            <Badge variant="secondary">
              {t(`sources.flavor.${source.flavor}`)}
            </Badge>
          </div>
          {source.supplierName && (
            <div className="text-muted-foreground truncate text-sm">
              {source.supplierName}
            </div>
          )}
        </div>
        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("common.edit")}
            onClick={onEdit}
          >
            <PencilIcon className="text-muted-foreground size-4" />
          </Button>
          <DeleteSourceButton
            itemId={source.producesItem}
            sourceId={source.id}
          />
        </div>
        <ChevronDown
          className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180"
          aria-hidden="true"
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="bg-muted/20 flex flex-col gap-4 border-t px-4 py-3">
          <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {source.minimumQuantity != null && (
              <DetailItem icon={<Hash className="size-4" />}>
                {t("sources.minQty", {
                  qty: formatNumber(Number(source.minimumQuantity)),
                })}
              </DetailItem>
            )}
            {source.lotMultiple != null && (
              <DetailItem icon={<Package className="size-4" />}>
                {t("sources.lotMultiple", { n: source.lotMultiple })}
              </DetailItem>
            )}
            <DetailItem icon={<CalendarDays className="size-4" />}>
              {t("sources.leadTimeDays", { days: source.leadTime ?? 0 })}
            </DetailItem>
          </div>

          {source.components && source.components.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {source.components.map((c) => (
                <li
                  key={c.id}
                  className="bg-background flex items-center justify-between gap-3 rounded-md px-3 py-2"
                >
                  <Link
                    to={`/items/${c.item}`}
                    className="flex min-w-0 flex-col text-sm"
                  >
                    <span className="text-foreground truncate font-medium hover:underline">
                      {c.itemName || `#${c.item}`}
                    </span>
                    <span className="text-muted-foreground flex items-center gap-1 truncate text-xs">
                      <MapPin className="size-3" />
                      {locationName(c.location)}
                      {c.consumeOffset ? (
                        <>
                          <Layers className="ml-2 size-3" />
                          {t("sources.consumedBeforeReceipt", {
                            days: c.consumeOffset,
                          })}
                        </>
                      ) : null}
                    </span>
                  </Link>
                  <Badge variant="secondary" className="tabular-nums">
                    {formatNumber(Number(c.quantity))}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function DeleteSourceButton({
  itemId,
  sourceId,
}: {
  itemId: number;
  sourceId: number;
}) {
  const { t } = useTranslation();
  const del = useDeleteSource(itemId);
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={t("common.delete")}
      disabled={del.isPending}
      onClick={() =>
        del.mutate(sourceId, {
          onError: (err) => {
            if (err instanceof ApiError && err.status === 409) {
              toast.error(t("sources.deleteBlocked"));
            } else {
              toast.error(t("sources.deleteError"));
            }
          },
        })
      }
    >
      <Trash2Icon className="text-muted-foreground size-4" />
    </Button>
  );
}

function DetailItem({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="truncate">{children}</span>
    </div>
  );
}
